import { pickUserProfilePictureProps, UserProfilePicture } from "@common/components/ProfilePicture/component";
import { EMPTY_CHART_CONFIG } from "@common/controls/Chart/lib";
import { type Media } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import date from "@common/lib/date";
import { formatPercentDisplay } from "@common/lib/format";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import React from "react";
import { type ReportData } from "../../types";
import { type ProviderProps, type TotalSalesBaseRequest } from "../types";
import CommonStaticProvider, { type StaticProviderReportData } from "./CommonStaticProvider";

export type ConversionsByTrial = {
  trialId: string;
  trialName: string;
  completedTrials: number;
  conversions: number;
};
export type ConversionsByTrialRequest = TotalSalesBaseRequest;

export type ConversionsByTrialDetail = {
  userId: number;
  userFirstName: string;
  userLastName: string;
  userProfilePictureMediaProfileId: string | null;
  userProfilePictureMediaSourceKey: Media.Source | null;
  userProfilePictureMediaSourceId: string | null;
  domain: string;
  createdOnDate: string;
  trialEndDate: string;
  canceledOnDate: string;
};
export type ConversionsByTrialDetailRequest = TotalSalesBaseRequest;

export type Props = Omit<ProviderProps, "id">;
export const CONVERSIONS_BY_TRIAL = "d08874d0-4338-4428-aaa6-e392b41e7345";
export const CONVERSIONS_BY_TRIAL_DETAIL = "cf4b62c0-1531-418f-b81f-b2eca32c1b40";

const ConversionsByTrialProvider = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const fields = React.useMemo(
    (): Data.Api.FormField[] => [
      {
        formControlKey: "DateRange",
        formFieldId: "dateRange",
        formFieldKey: "Date",
        label: i18n.dateRange,
        plainTextValue: `{"from": "${date.extractDate(date.addMonth(new Date(), -1))}","to": "${date.extractDate(
          new Date()
        )}"}`
      }
    ],
    [i18n]
  );

  const getChartData = React.useCallback(
    (id, data) => {
      return id === CONVERSIONS_BY_TRIAL
        ? api.getConversionsByTrial(data as any)
        : api.getConversionsByTrialDetail(data as any);
    },
    [api]
  );

  const getChildrenArgs = React.useCallback(
    (args: StaticProviderReportData): ReportData => {
      if (args.drillDownInfo.length) {
        return {
          ...args,
          id: CONVERSIONS_BY_TRIAL_DETAIL,
          config: EMPTY_CHART_CONFIG,
          getChartData,
          gridColumns: () => [
            {
              dataType: "string",
              key: "userFirstName",
              label: i18n.user,
              data: (x) => {
                return <UserProfilePicture {...pickUserProfilePictureProps(x)} />;
              }
            },
            {
              dataType: "url",
              key: "domain",
              label: i18n.website
            },
            {
              dataType: "dateTime",
              key: "createdOnDate",
              label: i18n.created
            },
            {
              dataType: "dateTime",
              key: "trialEndDate",
              label: i18n.trialEnd
            },
            {
              dataType: "dateTime",
              key: "canceledOnDate",
              label: i18n.canceled
            }
          ],
          gridConfig: { columns: [], pageSize: 25, key: "trial" },
          hasGrid: true,
          isChartHidden: true,
          isLoading: false,
          name: i18n.detail,
          type: "Bar"
        };
      }

      return {
        ...args,
        id: CONVERSIONS_BY_TRIAL,
        config: {
          aspectRatio: 2.5,
          colors: [],
          drillDownKey: "trialId",
          drillDownLabel: "trialName",
          legend: "hide",
          xAxis: {
            key: "trialName"
          },
          yAxis: [{ key: "conversions", color: "{$Blue}", label: i18n.conversions }],
          totalRecords: 25,
          yNoData: "hide"
        },
        getChartData,
        gridColumns: () => [
          {
            dataType: "string",
            key: "trialName",
            label: i18n.trial
          },
          {
            dataType: "string",
            key: "conversionRate",
            label: i18n.conversionRate,
            data: (x) => formatPercentDisplay(x.completedTrials > 0 ? x.conversions / x.completedTrials : 0)
          },
          {
            dataType: "number",
            key: "conversions",
            label: i18n.conversions
          },
          {
            dataType: "number",
            key: "completedTrials",
            label: i18n.completedTrials
          }
        ],
        gridConfig: { columns: [], pageSize: 25, key: "trial" },
        hasGrid: true,
        isLoading: false,
        name: i18n.conversionsByTrial,
        type: "Bar"
      };
    },
    [i18n]
  );

  return (
    <CommonStaticProvider
      badges={["StartDate"]}
      category="Host"
      drillDownInfo={{
        key: "trialId",
        reportId: CONVERSIONS_BY_TRIAL_DETAIL,
        reportName: i18n.conversionsByTrial,
        label: i18n.trial
      }}
      fields={fields}
      isLoading={false}
      onLoaded={props.onLoaded}
    >
      {(args) => props.children(getChildrenArgs(args))}
    </CommonStaticProvider>
  );
};

export default ConversionsByTrialProvider;
